import moment from "moment";
import get from "lodash/get";
import has from "lodash/has";
import find from "lodash/find";
import last from "lodash/last";
import pick from "lodash/pick";
import actions from "./actions";
import * as dateUtil from "../utils/date";
import { maybeWithNoEntries, normalizeEntries, padDayFor } from "../utils/entry";
import sentry from "../utils/sentry";

import { fetchMostRecentEntry, getInitialDate, getTotalHours } from "./userActions";

/**
 * @todo this does a lot and should probably be broken up
 */
export function fetchEntriesAndAdvancePage() {
  return async function _fetchAllEntries(dispatch, getState) {
    dispatch(_setAPIFilters);

    const state = getState();

    const { api } = state;

    const previousVersion = get(state, "entries.version");

    const filters = state.historyFilters.apiFilters;

    dispatch(_fetchingEntries());

    const loadEntries = entriesNormalizerFor(state);

    try {
      const response = await api.getTimeEntries(filters);

      // We might switch historyContext or something else while this request
      // is happening, if so, let's check.
      const currentVersion = get(getState(), "entries.version");

      if (previousVersion !== currentVersion) {
        if (process.env.NODE_ENV === "development") {
          console.debug("discarding fetchEntriesRequest; %s !== %s", previousVersion, currentVersion);
        }

        return;
      }

      const hasMore = _getHasMore(state, filters, response);

      await dispatch(_refreshWorkedSubmittedHours(response, previousVersion));

      const noEntriesOptions = {
        hasMore,
        page: get(state, "entries.page"),
        workingHours: get(state, "settings.workingHours"),
      };

      const entries = maybeWithNoEntries(
        loadEntries(response),
        filters,
        noEntriesOptions,
      );

      const paddingResponse = await _getPaddingResponse(api, entries, filters);

      const paddingEntries = loadEntries(paddingResponse);

      dispatch(_setHasMoreEntries(hasMore, previousVersion));
      dispatch(_advancePage(previousVersion));
      dispatch(_setEntries(entries, previousVersion));
      dispatch(_setDayGroupsFromPadding(paddingEntries, previousVersion));
      dispatch(_fetchedEntries(previousVersion));
    } catch (error) {
      console.error(error);

      sentry.logException(error);
    }
  };
}

export function resetSubmitGroup() {
  return { type: "SUBMIT_GROUP_RESET" };
}

export function toggleSubmitGroupEntries(entries, enabled) {
  return { type: "SUBMIT_GROUP_TOGGLE_ENTRIES", entries, enabled: Boolean(enabled) };
}

export function toggleSubmitGroupSelectAll(enabled) {
  return function(dispatch, getState) {
    const entries = get(getState(), "entries.entries", []).filter(function(entry) {
      return entry.hours > 0 && !entry.submitted;
    });

    dispatch(toggleSubmitGroupEntries(entries, enabled));
  }
}

export function toggleSubmitGroupEntry(entry, enabled) {
  return { type: "SUBMIT_GROUP_TOGGLE_ENTRY", entry, enabled: Boolean(enabled) };
}

export function refreshEntries({ because, previousFilters } = {}) {
  return async function(dispatch) {
    await dispatch(clearHistoryEntries({ because, previousFilters }));
    await dispatch(getInitialDate());
    await dispatch(getTotalHours());
    await dispatch(fetchMostRecentEntry());
    await dispatch(fetchEntriesAndAdvancePage({ initial: true }));
  };
}

export function setHistoryContext(name) {
  return function(dispatch, getState) {
    const previousFilters = getState().historyFilters;

    dispatch({ type: actions.SET_HISTORY_CONTEXT, name });

    dispatch(refreshEntries({ because: "historyContext", previousFilters }));
  };
}

export function setAllUntracked() {
  return function(dispatch) {
    dispatch({ type: actions.SET_HISTORY_ALL_UNTRACKED });

    dispatch(refreshEntries());
  };
}

export function setHistoryFilters(filters) {
  return { type: actions.SET_HISTORY_FILTERS, filters };
}

export function clearHistoryFilters() {
  return function(dispatch) {
    dispatch({ type: actions.CLEAR_HISTORY_FILTERS });

    dispatch(refreshEntries());
  };
}

export function setHistoryStartDate(startDate) {
  return { type: actions.SET_START_DATE, startDate };
}

export function unsetHistoryStartDate() {
  return { type: actions.UNSET_START_DATE };
}

export function setHistoryEndDate(endDate) {
  return { type: actions.SET_END_DATE, endDate };
}

export function unsetHistoryEndDate() {
  return { type: actions.UNSET_END_DATE };
}

export function openFilterModal() {
  return { type: actions.HISTORY_FILTER_MODAL_OPEN };
}

export function closeFilterModal() {
  return { type: actions.HISTORY_FILTER_MODAL_CLOSE };
}

export function toggleFilterModal() {
  return { type: actions.HISTORY_FILTER_MODAL_TOGGLE };
}

export function selectHistoryStartDate(startDate) {
  return async function(dispatch) {
    dispatch(setHistoryStartDate(startDate));
    dispatch(unsetHistoryEndDate());
    await dispatch(refreshEntries());
  }
}

function _setHistorySortDir(sortDir) {
  return async function(dispatch, getState) {
    if (getState().historyFilters.sortDir !== sortDir) {
      dispatch({ type: actions.SET_SORT_DIR, sortDir });
    }
  };
}

export function setHistorySortAscending() {
  return _setHistorySortDir("asc");
}

export function setHistorySortDescending() {
  return _setHistorySortDir("desc");
}

export function clearHistoryEntries({ because, previousFilters } = {}) {
  return function(dispatch, getState) {
    const current = getState().historyFilters;

    const filters = { previous: previousFilters, current };

    return dispatch({ type: actions.CLEAR_ENTRIES, because, filters });
  }
}

function _getHasMore(state, filters, response) {
  const responseTotal = get(response, "total", 0);
  const requestTotal = _getRequestTotal(filters);

  return responseTotal > requestTotal;
}

function _getRequestTotal(filters) {
  return get(filters, "from", 0) + get(filters, "size", 0);
}

function _setHasMoreEntries(hasMore, version) {
  return { type: actions.SET_HAS_MORE_ENTRIES, hasMore, version };
}

function _advancePage(version) {
  return { type: actions.ADVANCE_CURRENT_PAGE, version };
}

function _fetchingEntries() {
  return { type: actions.FETCHING_ENTRIES };
}

function _fetchedEntries(version) {
  return { type: actions.FETCHED_ENTRIES, version };
}

function _setEntries(entries, version) {
  return { type: actions.SET_ENTRIES, entries, version };
}

function _setDayGroups(dayGroups, version) {
  return { type: actions.SET_DAY_GROUPS, dayGroups, version };
}

function _setDayGroupsFromPadding(paddingEntries, version) {
  return function(dispatch, getState) {
    const state = getState();

    const entries = state.entries.entries;

    const workingHours = state.settings.workingHours;

    const dayGroups = _generateDayGroups(entries, paddingEntries, workingHours);

    return dispatch(_setDayGroups(dayGroups, version));
  };
}

function _refreshWorkedSubmittedHours(filteredResponse, previousVersion) {
  return async function(dispatch, getState) {
    const state = getState();

    const filters = state.historyFilters.apiFilters;

    try {
      const hoursResponse = await state.api.getTimeEntries({
        ...filters,
        size: 0,
        submitted: true
      });

      const [hours, submittedHours] = [filteredResponse, hoursResponse].map((resp) => get(resp, "aggregations.hours.value"));

      dispatch({
        type: actions.SET_WORKED_HOURS,
        hours,
        submittedHours,
        version: previousVersion,
      });
    } catch (error) {
      sentry.logException(error);
    }
  }
}

function _setAPIFilters(dispatch, getState) {
  const state = getState();
  const size = state.entries.pageSize;
  const from = state.entries.page * size;

  const topLevelFilters = pick(state.historyFilters, [
    "startDate", "endDate", "sortDir", "sortBy"
  ]);

  const raw = {
    size, from,
    ...state.historyFilters.filters,
    ...topLevelFilters,
  };

  const payload = {};

  for (const [key, value] of Object.entries(raw)) {
    if (value == null) {
      continue;
    }

    if (typeof value.getId === "function") {
      payload[`${key}Id`] = value.getId();
    } else if (typeof value === "function") {
      payload[key] = value();
    } else if (key === "serviceRequest") {
      payload.serviceRequestId = value.srNumber;
    } else {
      payload[key] = value;
    }
  }

  dispatch({ type: actions.SET_API_FILTERS, payload });
}

/**
 * A curry function for normalizing an array of entries out of a response.
 *
 * It pulls what we need out of the redux state and returns a function
 * that simply takes one argument: a response.
 *
 * @param {object} state
 * @return {function}
 */
function entriesNormalizerFor(state) {
  const categories = get(state, "categories.categoriesList", []);

  return function _loadEntries(response) {
    return normalizeEntries(response, categories);
  };
}

async function _getPaddingResponse(api, entries, filters) {
  const lastDay = padDayFor(entries, filters);

  if (!lastDay) {
    return null;
  }

  return await api.getTimeEntries({
    ...filters,
    size: 1000000,
    from: 0,
    startDate: lastDay,
    endDate: lastDay
  });
}

/**
 * @todo Make this less brittle.
 */
function _generateDayGroups(entries, paddingEntries, workingHours) {
  const dayGroups = {};

  for (const entry of entries) {
    const entryDay = dateUtil.dateToApiString(entry.entryDate);

    if (!dayGroups[entryDay]) {
      const workDay = workingHours[dateUtil.getDayNumber(entry.entryDate)];

      const { key, actualDate, entryDate, stringDate } = entry;

      dayGroups[entryDay] = {
        key, actualDate, entryDate, stringDate,
        isWorkDay: workDay.enabled,
        workDay: workDay,
        entries: [entry],
        hoursWorked: entry.hours
      };
    } else {
      dayGroups[entryDay].entries.push(entry);
      dayGroups[entryDay].hoursWorked += entry.hours;
    }
  }

  // So we don't ever show the user a partial day's worth of entries. If the user
  // can see a day, they are seeing every entry available for that day.
  // Several assumptions are made about paddingEntries:
  // - Every entry has the same entryDate
  // - Every entryDate overlaps with an entryDate in entries (but sometimes not??)
  if (paddingEntries && paddingEntries.length > 0) {
    const paddedDay = dateUtil.dateToApiString(paddingEntries[0].entryDate);

    if (dayGroups[paddedDay]) {
      const dayGroup = dayGroups[paddedDay];

      const entryIds = dayGroup.entries.map(({ id }) => id);

      for (const paddingEntry of paddingEntries) {
        const alreadyExists = entryIds.includes(paddingEntry.id);

        // dont show duplicate entries
        if (!alreadyExists) {
          dayGroups[paddedDay].entries.push(paddingEntry);
          dayGroups[paddedDay].hoursWorked += paddingEntry.hours;
        }
      }
    }
  }

  return Object.values(dayGroups);
}



// WEBPACK FOOTER //
// ./app/actions/historyActions.js